import { trpc } from '@/trpcClient';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { toast } from 'sonner';

const POLL_INTERVAL_MS = 30_000;
const TOAST_ID = 'led-panel-transcoder';

/**
 * Background worker for Painel LED materials: picks up videos that still need a version sized
 * and positioned for the panel (media_led_panel_variant 'pending'), converts them here in the
 * browser with ffmpeg.wasm and uploads the result. The campaign keeps the original media - the
 * server just delivers the processed file to the TV box once it is ready.
 *
 * Renders nothing; mounted once in BaseLayout for logged-in users.
 */
export function LedPanelTranscoder() {
  const queryClient = useQueryClient();
  const claimMutation = useMutation(trpc.media.ledPanelVariantClaim.mutationOptions());
  const completeMutation = useMutation(trpc.media.ledPanelVariantComplete.mutationOptions());
  const busyRef = useRef(false);

  // Mutation objects change identity every render - keep the latest in a ref for the interval
  const mutationsRef = useRef({ claim: claimMutation.mutateAsync, complete: completeMutation.mutateAsync });
  mutationsRef.current = { claim: claimMutation.mutateAsync, complete: completeMutation.mutateAsync };

  useEffect(() => {
    let cancelled = false;

    const warnBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
    };
    // Closing/reloading the tab aborts the download/upload in progress - that is not a failure of
    // the video: leave it 'processing' so another browser takes it over after it goes stale,
    // instead of marking it 'failed' (which would need a manual retry)
    let pageUnloading = false;
    const markUnloading = () => {
      pageUnloading = true;
    };
    window.addEventListener('pagehide', markUnloading);

    const runOnce = async () => {
      if (busyRef.current || cancelled || document.visibilityState !== 'visible') return;
      busyRef.current = true;

      let job: Awaited<ReturnType<typeof mutationsRef.current.claim>> = null;
      try {
        job = await mutationsRef.current.claim({ kinds: ['mp4', 'esp32-bin'] });
      } catch {
        // Offline, logged out, missing scope... just try again on the next tick
      }

      if (!job) {
        busyRef.current = false;
        return;
      }

      const format =
        job.kind === 'esp32-bin'
          ? `${job.geometry.widthPx}×${job.geometry.heightPx} · ${job.geometry.fps} fps (ESP32)`
          : `${job.geometry.widthPx}×${job.geometry.heightPx} em ${job.geometry.offsetXPx},${job.geometry.offsetYPx}`;
      const label = `Preparando vídeo para o painel LED: ${job.mediaName} · ${format}`;
      toast.loading(label, { id: TOAST_ID, description: 'Não feche esta aba até terminar.', duration: Infinity });
      window.addEventListener('beforeunload', warnBeforeUnload);

      try {
        const { transcodeForLedPanel, transcodeForEsp32 } = await import('./transcode');
        let lastPercent = -1;
        const onProgress = (ratio: number) => {
          const percent = Math.round(ratio * 100);
          if (percent !== lastPercent) {
            lastPercent = percent;
            toast.loading(`${label} (${percent}%)`, { id: TOAST_ID, description: 'Não feche esta aba até terminar.' });
          }
        };
        const output =
          job.kind === 'esp32-bin'
            ? await transcodeForEsp32(job.sourceUrl, job.sourceExtension, job.geometry, onProgress)
            : await transcodeForLedPanel(job.sourceUrl, job.sourceExtension, job.geometry, onProgress);

        const formData = new FormData();
        Object.entries(job.upload.fields ?? {}).forEach(([key, value]) => formData.append(key, value));
        formData.append('file', output);
        const uploadResponse = await fetch(job.upload.url, { method: 'POST', body: formData });
        if (!uploadResponse.ok) {
          throw new Error(`Falha ao enviar o vídeo processado (HTTP ${uploadResponse.status})`);
        }

        await mutationsRef.current.complete({ variantId: job.variantId, kind: job.kind, status: 'ready' });
        toast.success('Vídeo do painel LED pronto', {
          id: TOAST_ID,
          description: `${job.mediaName} · ${format}`,
          duration: 4000,
        });
        // Let the "pronto" message be seen before the next video (if any) takes the toast over
        await new Promise((resolve) => window.setTimeout(resolve, 2500));
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        if (pageUnloading) return;
        console.error('[LedPanelTranscoder] Failed to process variant', job.variantId, error);
        await mutationsRef.current
          .complete({ variantId: job.variantId, kind: job.kind, status: 'failed', errorMessage: message.slice(0, 500) })
          .catch(() => {});
        toast.error('Não foi possível preparar o vídeo do painel LED', {
          id: TOAST_ID,
          description: `${job.mediaName}: ${message.slice(0, 200)}`,
          duration: 8000,
        });
        await new Promise((resolve) => window.setTimeout(resolve, 6000));
      } finally {
        window.removeEventListener('beforeunload', warnBeforeUnload);
        busyRef.current = false;
        // Refresh the "Vídeos para o painel" list if the material screen is open
        void queryClient.invalidateQueries({ queryKey: trpc.material.ledPanelVariants.queryKey() });
      }

      // There may be more videos waiting - don't wait for the next tick
      if (!cancelled) void runOnce();
    };

    void runOnce();
    const interval = window.setInterval(() => void runOnce(), POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
      window.removeEventListener('pagehide', markUnloading);
    };
  }, []);

  return null;
}
